import { UserDto } from '@linka/domain/dtos';
import React, { createContext, startTransition, useLayoutEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { EventDto, EventRole } from '@/api/services/api/generated/data-contracts';

import { useEvent } from '../hooks/use-event';
import { useMe } from '../hooks/use-user-events';

export interface PageRequireAuthContextProps extends UsePermissionsTeamMembers {}

export const PageRequireAuthContext = createContext({} as PageRequireAuthContextProps);

interface PageRequireAuthProps {
  children: React.ReactNode;
}

export const PageRequireAuth = ({ children }: PageRequireAuthProps) => {
  const { eventId = '' } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { loggedUserRoleInTeamMembers, loggedUserIsEventOwner, loggedUserInfo, eventDetails, isAdmin } =
    useEventPermissions();
  const { isAuthorized } = useAuthorization();
  const ready = Boolean(loggedUserRoleInTeamMembers || (loggedUserInfo && eventDetails));

  useLayoutEffect(() => {
    if (!ready) return;

    startTransition(() => {
      if (loggedUserRoleInTeamMembers?.includes('CHECK_IN_OPERATOR') && !pathname.includes('/participants')) {
        navigate(`/manage/${eventId}/participants/list-participants`);
      } else if (loggedUserRoleInTeamMembers?.includes('BELONGINGS_OPERATOR')) {
        navigate(`/manage/${eventId}/participants/belongings-control`);
      }
    });
  }, [loggedUserRoleInTeamMembers, navigate, eventId, ready]);

  if (ready && !isAuthorized) return null;

  return (
    <PageRequireAuthContext.Provider
      value={{
        loggedUserRoleInTeamMembers,
        loggedUserIsEventOwner,
        loggedUserInfo,
        eventDetails,
        isAdmin,
      }}
    >
      {children}
    </PageRequireAuthContext.Provider>
  );
};

interface UsePermissionsTeamMembers {
  loggedUserRoleInTeamMembers: EventRole[] | undefined;
  loggedUserIsEventOwner: boolean | undefined;
  loggedUserInfo: UserDto | undefined;
  eventDetails: EventDto;
  isAdmin: boolean;
}

export const useEventPermissions = (): UsePermissionsTeamMembers => {
  const { eventId = '' } = useParams();
  const { data: eventDetails } = useEvent(eventId);
  const { data: loggedUserInfo } = useMe();
  const findLoggedUserInTeamMembers = eventDetails?.teamMembers.filter(
    (teamMember) => teamMember.user?.id === loggedUserInfo?.id,
  );
  const loggedUserRoleInTeamMembers =
    findLoggedUserInTeamMembers.length > 0 ? findLoggedUserInTeamMembers[0].roles : undefined;
  const loggedUserIsEventOwner = loggedUserRoleInTeamMembers?.includes('OWNER');
  const isAdmin = Boolean(
    loggedUserIsEventOwner ||
      loggedUserRoleInTeamMembers?.includes('ADMINISTRATOR') ||
      loggedUserRoleInTeamMembers?.includes('OWNER'),
  );

  return {
    loggedUserRoleInTeamMembers,
    loggedUserIsEventOwner,
    loggedUserInfo,
    isAdmin,
    eventDetails,
  };
};

const useAuthorization = () => {
  const { loggedUserRoleInTeamMembers, loggedUserIsEventOwner, loggedUserInfo, eventDetails } = useEventPermissions();
  const { pathname } = useLocation();
  const isParticipantsPath = pathname.includes('participants');
  const isBelongingsPath = pathname.includes('belongings-control');
  const isBelongingsOperatorOnBelongings =
    loggedUserRoleInTeamMembers?.includes('BELONGINGS_OPERATOR') && isBelongingsPath;
  const isCheckInOperatorOnParticipants =
    loggedUserRoleInTeamMembers?.includes('CHECK_IN_OPERATOR') && isParticipantsPath;
  const isOwner =
    loggedUserIsEventOwner ||
    loggedUserRoleInTeamMembers?.includes('ADMINISTRATOR') ||
    loggedUserRoleInTeamMembers?.includes('OWNER');
  const isAuthorized = isBelongingsOperatorOnBelongings || isCheckInOperatorOnParticipants || isOwner;

  return {
    loggedUserRoleInTeamMembers,
    loggedUserInfo,
    isAuthorized,
    eventDetails,
  };
};
